import { resetDirty } from "@frui.ts/dirtycheck";
import type { ScreenBase } from "@frui.ts/screens/dist";
import { Router } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import { Modules } from "config";
import type PayGateTransaction from "entities/payGateTransaction";
import PayGateTransactionsFilter, { PayGateTransactionsFilterValidationRules } from "entities/filters/payGateTransactionsFilter";
import type { IResults } from "data/types";
import { computed, observable } from "mobx";
import TurnoverRepository from "repositories/turnoverRepository";
import LocalizationService from "services/localizationService";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import ListViewModel from "viewModels/listViewModel";
import type Month from "../../../entities/month";
import MonthOptionsService from "services/monthOptionsService";
import { isFilterEmpty } from "utils/filters";
import { findMonthOptionByDate } from "utils/monthOption";

const navigationName = "paygate-transactions";

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class PayGateTransactionsViewModel extends ListViewModel<
  PayGateTransaction,
  ScreenBase,
  ScreenBase,
  PayGateTransactionsFilter
> {
  @observable private sourceMonths: Month[];
  readonly modelName = "payGateTransaction";

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get startOfMonthOptions() {
    return this.monthOptionService.startOfMonthOptions;
  }

  @computed
  get endOfMonthOptions() {
    return this.monthOptionService.endOfMonthOptions;
  }

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: TurnoverRepository,
    private monthOptionService: MonthOptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected createFilter() {
    return attachAutomaticValidator(new PayGateTransactionsFilter(), PayGateTransactionsFilterValidationRules);
  }

  protected resetFilterValues(filter: PayGateTransactionsFilter) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);
  }

  // Method overload to prevent validate filters when are empty
  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  protected fetchData(): Promise<IResults<PayGateTransaction>> {
    if (!isFilterEmpty(this.appliedFilter)) {
      return this.repository.getAllPayGateTransactions(this.appliedFilter);
    } else {
      return Promise.resolve({
        items: [],
      });
    }
  }

  protected parseFilterParam(paramName: keyof PayGateTransactionsFilter | string, value: any) {
    switch (paramName) {
      case "sinceDate":
        return findMonthOptionByDate(value, this.startOfMonthOptions)?.id;
      case "untilDate":
        return findMonthOptionByDate(value, this.endOfMonthOptions)?.id;
    }

    return super.parseFilterParam(paramName, value);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `payGateTransactions.${code}`, size);
  }
}
